import { ColumnDef } from "@tanstack/react-table";
import { TIVisibleFeatures, VisibleFeature } from "../../types/types";

// Define structure for generator props
export interface GeneratorProps {
    Entity_Name: string;
    Plant_ID: string;
    Plant_Name: string;
    Plant_State: string;
    County: string;
    Generator_ID: number;
    "Nameplate_Capacity_(MW)": number;
    "Net_Summer_Capacity_(MW)": number;
    "Net_Winter_Capacity_(MW)": number;
    Technology: string;
    Operating_Month: string;
    Operating_Year: string;
    Planned_Retirement_Month: string;
    Planned_Retirement_Year: string;
    Status: string;
    Planned_Derate_Year: string;
    Planned_Derate_Month: string;
    Planned_Uprate_Year: string;
    Planned_Uprate_Month: string;
}

// Define specific generator feature type
export interface GeneratorFeature extends VisibleFeature {
    properties: GeneratorProps;
}

// Type guard to check if a feature is a generator
export function isGeneratorFeature(
    feature: VisibleFeature
): feature is GeneratorFeature {
    return (
        feature.type === TIVisibleFeatures.generator && "properties" in feature
    );
}

// Transform a GeneratorFeature into a display format
export interface GeneratorDisplay {
    entityName: string;
    plantId: string;
    plantName: string;
    plantState: string;
    county: string;
    generatorId: number;
    nameplateCapacity: number;
    netSummerCapacity: number;
    netWinterCapacity: number;
    technology: string;
    operatingMonth: string;
    operatingYear: string;
    plannedRetirementMonth: string;
    plannedRetirementYear: string;
    status: string;
    plannedDerateYear: string;
    plannedDerateMonth: string;
    plannedUprateYear: string;
    plannedUprateMonth: string;
}

// Helper function to transform feature data into display format
export function transformGeneratorFeature(
    feature: GeneratorFeature
): GeneratorDisplay {
    return {
        entityName: feature.properties.Entity_Name || "N/A",
        plantId: feature.properties.Plant_ID || "N/A",
        plantName: feature.properties.Plant_Name || "N/A",
        plantState: feature.properties.Plant_State || "N/A",
        county: feature.properties.County || "N/A",
        generatorId: feature.properties.Generator_ID || 0,
        nameplateCapacity: feature.properties["Nameplate_Capacity_(MW)"] || 0,
        netSummerCapacity: feature.properties["Net_Summer_Capacity_(MW)"] || 0,
        netWinterCapacity: feature.properties["Net_Winter_Capacity_(MW)"] || 0,
        technology: feature.properties.Technology || "N/A",
        operatingMonth: feature.properties.Operating_Month || "N/A",
        operatingYear: feature.properties.Operating_Year || "N/A",
        plannedRetirementMonth:
            feature.properties.Planned_Retirement_Month || "N/A",
        plannedRetirementYear:
            feature.properties.Planned_Retirement_Year || "N/A",
        status: feature.properties.Status || "N/A",
        plannedDerateYear: feature.properties.Planned_Derate_Year || "N/A",
        plannedDerateMonth: feature.properties.Planned_Derate_Month || "N/A",
        plannedUprateYear: feature.properties.Planned_Uprate_Year || "N/A",
        plannedUprateMonth: feature.properties.Planned_Uprate_Month || "N/A",
    };
}

export const generatorColumns: ColumnDef<GeneratorDisplay>[] = [
    {
        accessorKey: "entityName",
        header: "Entity Name",
        enableSorting: true,
    },
    {
        accessorKey: "plantId",
        header: "Plant ID",
        enableSorting: true,
    },
    {
        accessorKey: "plantName",
        header: "Plant Name",
        enableSorting: true,
    },
    {
        accessorKey: "plantState",
        header: "Plant State",
        enableSorting: true,
    },
    {
        accessorKey: "county",
        header: "County",
        enableSorting: true,
    },
    {
        accessorKey: "generatorId",
        header: "Generator ID",
        enableSorting: true,
    },
    {
        accessorKey: "nameplateCapacity",
        header: "Nameplate Capacity",
        enableSorting: true,
    },
    {
        accessorKey: "netSummerCapacity",
        header: "Summer Capacity",
        enableSorting: true,
    },
    {
        accessorKey: "netWinterCapacity",
        header: "Winter Capacity",
        enableSorting: true,
    },
    {
        accessorKey: "technology",
        header: "Technology",
        enableSorting: true,
    },
    {
        accessorKey: "operatingMonth",
        header: "Operating Month",
        enableSorting: true,
    },
    {
        accessorKey: "operatingYear",
        header: "Operating Year",
        enableSorting: true,
    },
    {
        accessorKey: "plannedRetirementMonth",
        header: "Retirement Month",
        enableSorting: true,
    },
    {
        accessorKey: "plannedRetirementYear",
        header: "Retirement Year",
        enableSorting: true,
    },
    {
        accessorKey: "status",
        header: "Status",
        enableSorting: true,
    },
    {
        accessorKey: "plannedDerateYear",
        header: "Derate Year",
        enableSorting: true,
    },
    {
        accessorKey: "plannedDerateMonth",
        header: "Derate Month",
        enableSorting: true,
    },
    {
        accessorKey: "plannedUprateYear",
        header: "Uprate Year",
        enableSorting: true,
    },
    {
        accessorKey: "plannedUprateMonth",
        header: "Uprate Month",
        enableSorting: true,
    },
];
